"use client";
import policy from '../../../config/bite-usage.json';
import {isTestVisit} from './bite-reading-totals';
import {biteAudience,resolveBiteAudience} from './bite-conversion';
type Counter={page:string;source:string;event:string;value:number;cohort:'general'|'member'};
let counters=new Map<string,Counter>(),day='',lastPath='';
let timer:ReturnType<typeof setTimeout>|undefined;
export function usagePage(path:string) {
  if(path==='/'||path==='/bite')return 'landing';
  if(path.startsWith('/bite/examples/'))return 'example';
  if(path==='/bite/collection')return 'collection';
  if(path==='/bite/me')return 'account';
  if(path==='/bite/home')return 'home';
  if(path==='/bite/membership')return 'membership';
  if(path==='/bite/pro')return 'pricing';
  if(/^\/bite\/(card|share|shared)\//.test(path))return 'report';
  return /^\/bite\/[A-Z0-9.^-]+$/i.test(path)?'report':null;
}
export function usageSource() {
  const source=new URLSearchParams(location.search).get('utm_source')?.toLowerCase()||'';
  const named=policy.sources.find(name=>name!=='other'&&name!=='direct_or_unknown'&&(source===name||source.startsWith(name+'_')));
  if(named)return named;
  if(source)return 'other';
  let host='';try{host=new URL(document.referrer).hostname.toLowerCase();}catch{return 'direct_or_unknown';}
  if(host===location.hostname||['accounts.google.com','checkout.stripe.com','billing.stripe.com'].includes(host))return 'direct_or_unknown';
  const domains:Record<string,string[]>={bullstory:['bullstory.io'],instagram:['instagram.com'],facebook:['facebook.com','fb.com'],threads:['threads.net','threads.com'],telegram:['t.me','telegram.org'],google:['google.com','google.co.kr'],naver:['naver.com']};
  return Object.entries(domains).find(([,items])=>items.some(d=>host===d||host.endsWith('.'+d)))?.[0]||'other';
}
export function countBiteUsage(event:string,value=1,path=location.pathname) {
  const page=usagePage(path);
  if(!page||!policy.events.includes(event)||!Number.isSafeInteger(value)||value<=0)return;
  const today=new Date().toISOString().slice(0,10);
  if(day&&day!==today)flushBiteUsage();
  day=today;
  const source=usageSource(),cohort=biteAudience(),key=`${page}:${source}:${event}:${cohort}`,previous=counters.get(key);
  counters.set(key,{page,source,event,cohort,value:(previous?.value||0)+value});
  if(!timer)timer=setTimeout(flushBiteUsage,policy.publishMs);
}
export function flushBiteUsage() {
  clearTimeout(timer);timer=undefined;
  if(!counters.size)return;
  const packet={type:'usage_totals',eventId:crypto.randomUUID(),day,counters:[...counters.values()]};
  counters=new Map();
  const test=isTestVisit();
  const audience=resolveBiteAudience();
  const payload=(async()=>{
    const resolvedAudience=await audience;
    return JSON.stringify({measurement:'bullstory-v1',events:[{...packet,counters:packet.counters.map(({cohort,...c})=>({...c,...(cohort==='member'||resolvedAudience==='member'?{cohort:'member'}:resolvedAudience==='general'?{cohort:'general'}:{})})),isTest:await test}]});
  })();
  const send=async(attempt=0):Promise<void>=>{
    try {
      const response=await fetch('/api/analytics/event',{method:'POST',credentials:'omit',referrerPolicy:'no-referrer',keepalive:true,
        headers:{'Content-Type':'application/json'},body:await payload});
      if(response.status>=400&&response.status<500)return;
      if(!response.ok)throw new Error('usage_delivery');
    }catch{if(attempt<3)setTimeout(()=>void send(attempt+1),policy.retryMs);}
  };
  void send();
}
export function startBiteUsage(path:string) {
  void isTestVisit();
  if(lastPath!==path){lastPath=path;countBiteUsage('pageview',1,path);
    if(path==='/bite/me')countBiteUsage('account_view',1,path);
    if(path==='/bite/collection')countBiteUsage('collection_view',1,path);
    if(new URLSearchParams(location.search).get('payment')==='cancelled')countBiteUsage('checkout_cancel',1,path);
  }
  let visibleSince=document.visibilityState==='visible'?Date.now():0,scroll=0,ended=false;
  const updateScroll=()=>{scroll=Math.max(scroll,Math.min(100,Math.round((window.scrollY+innerHeight)/Math.max(document.documentElement.scrollHeight,innerHeight)*100)));};
  const publish=()=>{if(visibleSince){countBiteUsage('active_ms',Math.min(policy.dayMs,Date.now()-visibleSince),path);visibleSince=0;}flushBiteUsage();};
  const visibility=()=>{if(document.visibilityState==='hidden')publish();else visibleSince=Date.now();};
  const finish=()=>{if(ended)return;ended=true;countBiteUsage('dwell_samples',1,path);countBiteUsage('scroll_sum',scroll,path);publish();};
  document.addEventListener('visibilitychange',visibility);window.addEventListener('pagehide',finish);window.addEventListener('scroll',updateScroll,{passive:true});
  return ()=>{finish();document.removeEventListener('visibilitychange',visibility);window.removeEventListener('pagehide',finish);window.removeEventListener('scroll',updateScroll);};
}
